import React, { memo, useCallback, useMemo, useState } from "react";
import cx from "classnames";
import useQuery, { useRevalidate } from "../../../api/useQuery";
import Table from "@ui/Table";
import type { ColumnMetaData } from "@ui/Table/ColumnMetaData";
import Button from "@ui/Button";
import { spacings } from "@ui/Theme";
import { css } from "@linaria/core";
import Modal from "@ui/Modal";
import useModal from "@ui/lib/hooks/useModal";
import ModalForm from "@ui/Modal/Form";
import { usePatch, usePost } from "../../../api/useCall";
import InputV2 from "@ui/Input/InputV2";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { DeleteBinLine } from "@ui/IconV2/react";
import useToasts from "@ui/Toast/useToasts";
import { locale } from "../../../i18n/locale";
import { DateTime } from "luxon";
import useConfirmModal from "@ui/Modal/useConfirmModal";
import t from "../../../i18n";

type Keys = ReturnType<typeof useQuery<"/keys">>["body"];

const APIKeyTable = css`
  margin-bottom: ${spacings.s};
`;

export const LiveDataWorkflowAPIKey = memo(() => {
  const { addToast } = useToasts();
  const {
    modal: generateModalOpened,
    openModal: openGenerateModal,
    closeModal: closeGenerateModal,
  } = useModal();
  const confirm = useConfirmModal();
  const queryKeys = useQuery("/keys", {});
  const revalidateKeys = useRevalidate();
  const generateKey = usePost("/keys");
  const revokeKey = usePatch("/keys/revoke");
  const [generatedKey, setGeneratedKey] = useState<string | null>();
  const keys = useMemo(() => queryKeys.body, [queryKeys.body]);

  const revokeGeneratedKey = useCallback(
    (id: string) => {
      confirm({
        title: t("administration.application.api_key.modal.revoke.title"),
        body: (
          <AdditionnalMessage
            status="WARNING"
            text={t("administration.application.api_key.modal.revoke.text")}
          />
        ),
        okButtonVariant: "destructive",
        okButtonName: t("administration.application.api_key.modal.revoke.button"),
      })
        .then(confirmed => (confirmed ? revokeKey({ body: { id } }) : null))
        .then(
          response =>
            response &&
            addToast("success", t("administration.application.api_key.transaction.revoke.success")),
        )
        .catch(() =>
          addToast("error", t("administration.application.api_key.transaction.revoke.failure")),
        )
        .finally(() => revalidateKeys("/keys"));
    },
    [addToast, confirm, revalidateKeys, revokeKey],
  );

  const keyMetadatas = useMemo(() => {
    const m: { [key in keyof Keys[number] | "action"]: ColumnMetaData<Keys[number]> } = {
      key: {
        label: t("administration.application.api_key.column.api_key"),
        render: k => k.key,
      },
      createdAt: {
        label: t("administration.application.api_key.column.created_at"),
        render: k =>
          DateTime.fromISO(k.createdAt)
            .setLocale(locale)
            .toFormat(
              `dd LLL yyyy, ${locale === "en" ? "h" : "H"}:mm ${locale === "en" ? "a" : ""}`,
            ),
        maxWidth: "200px",
      },
      createdBy: {
        label: t("administration.application.api_key.column.created_by"),
        render: k => k.createdBy,
        maxWidth: "300px",
      },
      id: { hidden: true },
      lastUsedAt: { hidden: true },
      action: {
        maxWidth: "75px",
        alignment: "center",
        render: k => (
          <ButtonIconV2 onClick={() => revokeGeneratedKey(k.id)}>
            <DeleteBinLine size="m" />
          </ButtonIconV2>
        ),
      },
    };
    return m;
  }, [revokeGeneratedKey]);

  const computeRowKey = useCallback(({ id }: (typeof keys)[number]) => id, []);

  const submitGeneration = useCallback(
    () =>
      generateKey({})
        .then(res => {
          setGeneratedKey(res.body.key);
          addToast("success", t("administration.application.api_key.transaction.create.success"));
          return false;
        })
        .catch(() => {
          addToast("error", t("administration.application.api_key.transaction.create.failure"));
          return false;
        })
        .finally(() => false),
    [addToast, generateKey],
  );

  const copyGeneratedAPIKey = useCallback(
    () =>
      generatedKey
        ? window.navigator.clipboard
            .writeText(generatedKey)
            .then(() => {
              addToast("success", t("administration.application.api_key.transaction.copy.success"));
              setGeneratedKey(null);
              closeGenerateModal();
              return true;
            })
            .catch(() => {
              addToast("error", t("administration.application.api_key.transaction.copy.failure"));
              return true;
            })
        : false,
    [addToast, closeGenerateModal, generatedKey],
  );

  return (
    <div>
      {keys.length > 0 && (
        <Table
          datas={keys}
          metadata={keyMetadatas}
          computeRowKey={computeRowKey}
          className={cx(APIKeyTable)}
        />
      )}
      <Button variant="primary" onClick={() => openGenerateModal()}>
        {t("administration.application.api_key.modal.create.title")}
      </Button>
      <Modal
        title={t("administration.application.api_key.modal.create.title")}
        isOpen={generateModalOpened}
        onRequestClose={() => {
          closeGenerateModal();
          setGeneratedKey(null);
          void revalidateKeys("/keys");
        }}
        size="l"
      >
        <ModalForm
          onSubmit={generatedKey ? copyGeneratedAPIKey : submitGeneration}
          onRequestClose={() => {
            closeGenerateModal();
            setGeneratedKey(null);
            void revalidateKeys("/keys");
          }}
          okButtonName={
            generatedKey
              ? t("administration.application.api_key.modal.copy.button")
              : t("administration.application.api_key.modal.create.button")
          }
        >
          <AdditionnalMessage
            status="INFO"
            text={t("administration.application.api_key.modal.create.text")}
          />
          {generatedKey && (
            <InputV2
              label={t("administration.application.api_key.column.api_key")}
              value={generatedKey}
              readOnly
            />
          )}
        </ModalForm>
      </Modal>
    </div>
  );
});
